import type { Source } from '@casetrace/shared';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ImportStatusIndicator } from './import-status-indicator';
import {
  JOB_STATUS_LABEL,
  SOURCE_STATUS_LABEL,
  type SourceWithLatestJob,
} from './sources-panel-helpers';
import { SourceTypeIcon } from './source-type-icon';

type SourcesListSectionProps = {
  readonly rows: SourceWithLatestJob[];
  readonly isLoading: boolean;
  readonly isDeleting: boolean;
  readonly hasError: boolean;
  readonly onDelete: (sourceId: Source['id']) => void;
};

export function SourcesListSection({
  rows,
  isLoading,
  isDeleting,
  hasError,
  onDelete,
}: SourcesListSectionProps) {
  return (
    <section>
      <h3 className="text-xs font-medium uppercase tracking-wide text-[#5e6070]">Kilder</h3>
      {isLoading && <p className="mt-2 text-xs text-[#5e6070]">Laster kilder...</p>}
      {!isLoading && rows.length === 0 && (
        <p className="mt-2 text-xs text-[#5e6070]">Ingen kilder koblet til enda.</p>
      )}
      <div className="mt-2 flex flex-col gap-2">
        {rows.map(({ source, latestJob }) => (
          <div
            key={source.id}
            className="rounded-md border p-3"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-2 truncate text-sm font-medium text-[#ededf0]">
                  <SourceTypeIcon type={source.type} className="shrink-0" />
                  <span className="truncate">{source.navn}</span>
                </p>
                <p className="text-xs text-[#5e6070]">
                  Status: {SOURCE_STATUS_LABEL[source.status]}
                </p>
                {latestJob && (
                  <p className="text-xs text-[#5e6070]">
                    Jobb: {JOB_STATUS_LABEL[latestJob.status]}
                    {typeof latestJob.progress === 'number' ? ` (${latestJob.progress}%)` : ''}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <ImportStatusIndicator source={source} />
                <Button
                  className="px-2 py-1 text-xs text-danger/80"
                  variant="ghost"
                  disabled={isDeleting}
                  aria-label={`Slett kilde ${source.navn}`}
                  onClick={() => onDelete(source.id)}
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {hasError && (
        <p className="mt-2 text-xs text-danger/80">Kunne ikke starte import. Prøv igjen.</p>
      )}
    </section>
  );
}
